import { BreadCrumbs, Filter, HomeIcon, Input, MultiSelect } from '@isdd/idsk-ui-kit/index'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RELATION_TYPE } from '@isdd/metais-common/api'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { JOIN_OPERATOR, OPERATOR_SEPARATOR, RELATION_FILTER_PREFIX } from '@isdd/metais-common/constants'
import { DynamicFilterAttributes, ExtendedAttribute } from '@isdd/metais-common/components/dynamicFilterAttributes/DynamicFilterAttributes'
import { MetainformationColumns } from '@isdd/metais-common/componentHelpers/ci/getCiDefaultMetaAttributes'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { AttributeProfile } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { useSearchParams } from 'react-router-dom'
import { RowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { Languages } from '@isdd/metais-common/localization/languages'
import { join } from 'lodash'

import { CIFilterData } from '@/pages/ci/[entityName]/entity'
import { getCiHowToBreadCrumb, useGetEntityParamsFromUrl } from '@/componentHelpers/ci'
import { MainContentWrapper } from '@/components/MainContentWrapper'
import { ListWrapper } from '@/components/list-wrapper/ListWrapper'
import { CiListWizard } from '@/components/wizards/CiListWizard'
import { CiListContainer } from '@/components/containers/CiListContainer'

const SpravcaKSRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_spravca_AS}` as 'SpravcaKSRelType'

const ISVSKSRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.ISVS_realizuje_AS}` as 'ISVSKSRelType'
const ProjektKSRelType = `${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Projekt_realizuje_AS}` as 'ProjektKSRelType'

export interface KSFilterData extends CIFilterData {
    [SpravcaKSRelType]?: string
    [ISVSKSRelType]?: string
    [ProjektKSRelType]?: string
    'EA_Profil_AS_dostupnost_pre_externu_integraciu--eq'?: string
}
export interface KSFilterProps {
    defaultFilterValues: KSFilterData
    entityName: string
    codePrefix: string
    constraintsData: (EnumType | undefined)[] | undefined
    attributes: ExtendedAttribute[] | undefined
    attributeProfiles: AttributeProfile[] | undefined
    rowSelection: RowSelection<object>
}

export const ASFilter: React.FC<KSFilterProps> = ({
    defaultFilterValues,
    attributes,
    attributeProfiles,
    constraintsData,
    codePrefix,
    entityName,
    rowSelection,
}) => {
    const { t, i18n } = useTranslation()
    const [urlParams] = useSearchParams()
    const spravcaRelationFromUrl = urlParams.get(SpravcaKSRelType)
    const ISVSRelationFromUrl = urlParams.get(ISVSKSRelType)
    const ProjektRelationFromUrl = urlParams.get(ProjektKSRelType)
    const ProvidedForExtMigrationFromUrl = urlParams.get('EA_Profil_AS_dostupnost_pre_externu_integraciu--eq')
    const [providedForExtMigration, setProvidedForExtMigration] = useState(
        ProvidedForExtMigrationFromUrl
            ? ProvidedForExtMigrationFromUrl.includes(JOIN_OPERATOR)
                ? ProvidedForExtMigrationFromUrl.split(JOIN_OPERATOR)
                : [ProvidedForExtMigrationFromUrl]
            : [defaultFilterValues['EA_Profil_AS_dostupnost_pre_externu_integraciu--eq'] as string],
    )

    const providedForExtMigrationConstraints = constraintsData?.find((c) => c?.code === 'DOSTUPNOST_PRE_EXTERNU_INTEGRACIU')
    const optionsForSelects =
        providedForExtMigrationConstraints?.enumItems
            ?.filter((item) => item.valid)
            .map((item) => {
                const qualifier =
                    i18n.language === Languages.SLOVAK
                        ? item.qualifierName
                            ? `(${item.qualifierName})`
                            : ''
                        : item.qualifierNameEng
                        ? `(${item.qualifierNameEng})`
                        : ''
                return {
                    label: item.description ? `${item.description} ${qualifier}` : `${item.value} ${qualifier}`,
                    value: `${item.code}`,
                }
            }) ?? []

    return (
        <Filter<KSFilterData, object>
            rowSelection={rowSelection}
            defaultFilterValues={defaultFilterValues}
            form={({ register, filter, setValue, isOpen }) => {
                return (
                    <>
                        <CiLazySelect
                            ciType={'PO'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.PO_je_spravca_AS}`)}
                            name={SpravcaKSRelType}
                            setValue={setValue}
                            defaultValue={
                                spravcaRelationFromUrl
                                    ? spravcaRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? spravcaRelationFromUrl.split(JOIN_OPERATOR)
                                        : spravcaRelationFromUrl
                                    : defaultFilterValues[SpravcaKSRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={'ISVS'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.ISVS_realizuje_AS}`)}
                            name={ISVSKSRelType}
                            setValue={setValue}
                            defaultValue={
                                ISVSRelationFromUrl
                                    ? ISVSRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? ISVSRelationFromUrl.split(JOIN_OPERATOR)
                                        : ISVSRelationFromUrl
                                    : defaultFilterValues[ISVSKSRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />
                        <CiLazySelect
                            ciType={'Projekt'}
                            label={t(`${RELATION_FILTER_PREFIX}${OPERATOR_SEPARATOR}${RELATION_TYPE.Projekt_realizuje_AS}`)}
                            name={ProjektKSRelType}
                            setValue={setValue}
                            defaultValue={
                                ProjektRelationFromUrl
                                    ? ProjektRelationFromUrl.includes(JOIN_OPERATOR)
                                        ? ProjektRelationFromUrl.split(JOIN_OPERATOR)
                                        : ProjektRelationFromUrl
                                    : defaultFilterValues[ProjektKSRelType] ?? ''
                            }
                            isMulti
                            tabIndex={isOpen ? undefined : -1}
                        />

                        <Input
                            id="name"
                            label={t(`filter.${entityName}.name`)}
                            placeholder={t(`filter.namePlaceholder`)}
                            {...register('Gen_Profil_nazov')}
                        />

                        <Input
                            id="metais-code"
                            label={t('filter.metaisCode.label')}
                            placeholder={codePrefix}
                            {...register('Gen_Profil_kod_metais')}
                        />
                        <MultiSelect
                            options={optionsForSelects}
                            setValue={setValue}
                            id="provided-for-ext-migration"
                            label={providedForExtMigrationConstraints?.name}
                            {...register('EA_Profil_AS_dostupnost_pre_externu_integraciu--eq')}
                            placeholder={t('customAttributeFilter.value.placeholderSelectMulti')}
                            onChange={(newValue) => {
                                setValue(
                                    'EA_Profil_AS_dostupnost_pre_externu_integraciu--eq',
                                    Array.isArray(newValue) ? join(newValue, '+') : newValue,
                                )
                                setProvidedForExtMigration(newValue)
                            }}
                            value={providedForExtMigration}
                        />
                        <DynamicFilterAttributes
                            setValue={setValue}
                            defaults={defaultFilterValues}
                            filterData={{
                                attributeFilters: filter.attributeFilters ?? {},
                                metaAttributeFilters: filter.metaAttributeFilters ?? {},
                            }}
                            attributes={attributes as ExtendedAttribute[]}
                            attributeProfiles={attributeProfiles}
                            constraintsData={constraintsData}
                            isFocusable={isOpen}
                            ignoreInputNames={[MetainformationColumns.OWNER, 'EA_Profil_AS_dostupnost_pre_externu_integraciu']}
                        />
                    </>
                )
            }}
        />
    )
}

export const ASListPage: React.FC = () => {
    const { entityName: ciType } = useGetEntityParamsFromUrl()
    const { t } = useTranslation()

    const defaultFilterValues: KSFilterData = {
        Gen_Profil_nazov: '',
        Gen_Profil_kod_metais: '',
        [SpravcaKSRelType]: '',
        [ISVSKSRelType]: '',
        'EA_Profil_AS_dostupnost_pre_externu_integraciu--eq': '',
    }

    return (
        <>
            <CiListContainer<CIFilterData>
                entityName={ciType ?? ''}
                defaultFilterValues={defaultFilterValues}
                ListComponent={(props) => (
                    <>
                        <BreadCrumbs
                            withWidthContainer
                            links={[
                                { label: t('breadcrumbs.home'), href: '/', icon: HomeIcon },
                                ...getCiHowToBreadCrumb(props.entityName, t),
                                {
                                    label: t('titles.ciList', { ci: props.ciTypeData?.name }) ?? '',
                                    href: `/ci/${ciType}`,
                                },
                            ]}
                        />
                        {!props.isLoading && <CiListWizard />}
                        <MainContentWrapper>
                            <ListWrapper {...props} customFilter={(filterProps) => <ASFilter {...filterProps} />} />
                        </MainContentWrapper>
                    </>
                )}
            />
        </>
    )
}
